import React from "react";
import { motion } from "framer-motion";
import { User, Mail, Phone, Building2, Globe, HelpCircle } from "lucide-react";
import { useMultiStepForm, type ContactData } from "../MultiStepFormContext";
import { REFERRAL_SOURCES } from "../questionConfig";

export function ContactInfoStep() {
  const { contact, setContact, error } = useMultiStepForm();

  const inputFields: {
    field: keyof ContactData;
    label: string;
    icon: typeof User;
    type: string;
    placeholder: string;
    required: boolean;
  }[] = [
    {
      field: "name",
      label: "Full Name",
      icon: User,
      type: "text",
      placeholder: "Your name",
      required: true,
    },
    {
      field: "email",
      label: "Email",
      icon: Mail,
      type: "email",
      placeholder: "you@example.com",
      required: true,
    },
    {
      field: "phone",
      label: "Phone",
      icon: Phone,
      type: "tel",
      placeholder: "(555) 123-4567",
      required: false,
    },
    {
      field: "organization",
      label: "Company / Organization",
      icon: Building2,
      type: "text",
      placeholder: "Your company name",
      required: false,
    },
    {
      field: "website",
      label: "Website",
      icon: Globe,
      type: "url",
      placeholder: "https://yoursite.com",
      required: false,
    },
  ];

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-[var(--dug-text-primary)] ds-type-control-heading">
          Almost there!
        </h2>
        <p className="text-[var(--dug-text-muted)] mt-2 ds-type-body">
          Tell us how to reach you
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
        >
          {error}
        </motion.div>
      )}

      <div className="space-y-4">
        {inputFields.map((field, index) => {
          const Icon = field.icon;
          const isInvalid =
            field.required &&
            field.field === "email" &&
            contact.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email);

          return (
            <motion.div
              key={field.field}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <label className="block text-sm font-medium text-[var(--dug-text-primary)] mb-2">
                {field.label}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--dug-text-muted)]">
                  <Icon className="w-5 h-5" />
                </div>
                <input
                  type={field.type}
                  value={contact[field.field]}
                  onChange={(e) => setContact(field.field, e.target.value)}
                  placeholder={field.placeholder}
                  className={`
                    w-full pl-12 pr-4 py-3 rounded-xl border-2
                    bg-[var(--dug-bg-surface)] text-[var(--dug-text-primary)]
                    placeholder:text-[var(--dug-text-muted)]
                    focus:outline-none focus:ring-2 focus:ring-[var(--dug-coral)]/20
                    transition-all
                    ${isInvalid
                      ? "border-red-500 focus:border-red-500"
                      : "border-[var(--dug-border)] focus:border-[var(--dug-coral)]"
                    }
                  `}
                />
              </div>
              {isInvalid && (
                <p className="text-red-400  mt-1 ds-type-body">
                  Please enter a valid email address
                </p>
              )}
            </motion.div>
          );
        })}

        {/* Referral Source Dropdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: inputFields.length * 0.05 }}
        >
          <label className="block text-sm font-medium text-[var(--dug-text-primary)] mb-2">
            How did you hear about us?
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--dug-text-muted)]">
              <HelpCircle className="w-5 h-5" />
            </div>
            <select
              value={contact.referralSource}
              onChange={(e) => setContact("referralSource", e.target.value)}
              className={`
                w-full pl-12 pr-4 py-3 rounded-xl border-2
                bg-[var(--dug-bg-surface)] text-[var(--dug-text-primary)]
                border-[var(--dug-border)] focus:border-[var(--dug-coral)]
                focus:outline-none focus:ring-2 focus:ring-[var(--dug-coral)]/20
                transition-all appearance-none cursor-pointer
              `}
            >
              <option value="">Select an option...</option>
              {REFERRAL_SOURCES.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
            {/* Custom dropdown arrow */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--dug-text-muted)]">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
